import axios from 'axios';

import aesReduxConstants from '../constants';
import productsSelectors from '../selectors/productsSelectors';
import currentProductSelectors from '../selectors/currentProductSelectors';
import userSelectors from '../selectors/userSelectors';
import contextSelectors from '../selectors/contextSelectors';
import userUtils from '../utils/userUtils';
import {
  getProductNotifications,
  getClientProductNotifications,
  getProductNotificationCriteriaFilter,
} from '../apis/productNotificationApi';
import API_HOST from '../api';
import productEventApi from '../apis/productEventApi';
import clientApi from '../apis/clientApi';
import {
  AUTO_WEATHER_PRODUCT_TYPES,
  ENABLED_PRODUCT_TYPES,
} from '../utils/genericProduct/constants';

/**
 * ProductActions
 * holds all of the redux actions for product events
 */
const productEventActions = () => {
  const factory = {};
  const constants = aesReduxConstants;

  /**
   * Get ProductList Success
   * replaces the notification list with an array of notificiations.
   * Usually used inside of `getProductListAction`
   */
  factory.getProductListSuccess = (products) => ({
    type: constants.GET_PRODUCT_LIST_SUCCESS,
    products,
  });

  factory.getProductListError = (error) => {
    const { response } = error;
    const { data } = response;
    const { details } = data;

    const action = {
      type: constants.GET_PRODUCT_LIST_ERROR,
      error: {
        title: 'Error Getting Product List',
        message: details,
      },
    };

    return action;
  };

  factory.filterCurrentEvents = (events = []) => events.filter((event) => (moment(event.content.productTimeout).diff(new Date()) <= 0 ? -1 : 1));

  /**
   * Get ProductList List
   * sends a request to get product and updates the list with the product list from the api response
   */
  factory.getProductList = () => (dispatch, getState) => {
    const state = getState();
    const userId = contextSelectors.selectUserId(state);
    const clientId = contextSelectors.selectClientId(state);

    // get the max timeout first so that when you request the list you get the correct events times
    clientApi.getClientMaxProductTimeout(clientId)
      .then((response) => {
        const { data } = response;
        const { maxProductTimeout } = data;
        const sinceEpoch = moment().subtract(maxProductTimeout, 'minutes').format('X');
        const inContextSwitch = contextSelectors.selectInSwitchContext(state);

        dispatch({ type: constants.GET_PRODUCT_LIST });
        productEventApi.getProductList(clientId, userId, sinceEpoch, inContextSwitch)
          .then((response) => {
            const { data: products } = response;

            dispatch(factory.getProductListSuccess(products));
            // raw list of products to product details list
            dispatch(factory.getProductDetailsList(products));
          }, (error) => {
            dispatch(factory.getProductListError(error));
          });
      });
  };

  factory.getProductDetailsListSuccess = (productDetails) => ({
    type: constants.GET_PRODUCT_DETAILS_LIST_SUCCESS,
    productDetails,
  });

  factory.getProductDetailsList = (productEvents) => (dispatch) => {
    
    const productsDetailsPromises = productEvents.map((event, index) => {
      const {
        content,
      } = event;
      const {
        productDetailsUrl,
      } = content;

      return axios.get(productDetailsUrl, { ignoreLoadingBar: true })
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.log('[API DEBUG] Details #' + index + ' ERROR:', error);
          console.log('[API DEBUG] Details #' + index + ' URL was:', productDetailsUrl);
          // Return a resolved promise with empty data to not break the Promise.all
          return { data: null };
        });
    });

    axios.all(productsDetailsPromises).then((responses) => {
      const productDetails = responses.map((response) => response.data);

      productDetails.forEach((detail, idx) => {
        if (detail) {
          console.log('[API DEBUG] Detail #' + idx + ' severity:', detail.severity);
        }
      });

      // update the details list
      dispatch(factory.getProductDetailsListSuccess(productDetails));
    });
  };

  factory.getCurrentProductSuccess = () => ({
    type: constants.GET_CURRENT_PRODUCT_SUCCESS,
  });

  factory.getCurrentProductEventSuccess = (event) => ({
    type: constants.GET_CURRENT_PRODUCT_EVENT,
    event,
  });

  factory.getCurrentProductEventError = (error) => ({
    type: constants.GET_CURRENT_PRODUCT_EVENT_ERROR,
    // dont get caught by the middleware so its _error instead of error
    _error: error,
  });

  factory.getCurrentProductEvent = (productEventId, callback) => (dispatch, getState) => {
    const state = getState();
    const userId = contextSelectors.selectUserId(state);
    const clientId = contextSelectors.selectClientId(state);
    const inContextSwitch = contextSelectors.selectInSwitchContext(state);

    const cachedProductEvent = productsSelectors.selectProductEvent(state, productEventId);

    if (cachedProductEvent === null) {
      // get the product event object
      productEventApi.getProductEvent(productEventId, clientId, userId, inContextSwitch)
        .then((response) => {
          const productEvent = response.data;

          dispatch(factory.getCurrentProductEventSuccess(productEvent));
          if (typeof callback === 'function') callback(productEvent);
        }, (error) => {
          dispatch(factory.getCurrentProductEventError(error));
        });
    } else {
      dispatch(factory.getCurrentProductEventSuccess(cachedProductEvent));
      if (typeof callback === 'function') callback(cachedProductEvent);
    }
  };

  factory.getCurrentProductDetailsSuccess = (details) => ({
    type: constants.GET_CURRENT_PRODUCT_DETAILS,
    details,
  });

  factory.getCurrentProductDetailsError = (error) => ({
    type: constants.GET_CURRENT_PRODUCT_DETAILS_ERROR,
    error,
  });

  factory.getCurrentProductDetails = (productEvent, callback) => (dispatch, getState) => {
    const state = getState();
    const productDetailsCache = productsSelectors.selectProductDetail(state, productEvent.id);

    if (productDetailsCache === null) {
      productEventApi.getProductDetails(productEvent)
        .then((response) => {
          const {
            data,
          } = response;

          dispatch(factory.getCurrentProductDetailsSuccess(data));
          dispatch(factory.getCurrentProductNotifications());
          if (typeof callback === 'function') callback(data);
        });
    } else {
      dispatch(factory.getCurrentProductNotifications());
      dispatch(factory.getCurrentProductDetailsSuccess(productDetailsCache));
      if (typeof callback === 'function') callback(productDetailsCache);
    }
  };

  /**
   * Get Current Product
   */
  factory.getCurrentProduct = (productEventId) => (dispatch) => {
    // show that the current product is loading and the current product is not accurate
    dispatch({ type: constants.GET_CURRENT_PRODUCT });
    // get the product event
    dispatch(factory.getCurrentProductEvent(productEventId, (productEvent) => {
      dispatch(factory.getCurrentProductDetails(productEvent, () => {
        dispatch(factory.getCurrentProductSuccess());
      }));
    }));
  };

  factory.getClientsAvailableProductsSuccess = (products) => ({
    type: constants.GET_CLIENTS_AVAILABLE_PRODUCTS_SUCCESS,
    products,
  });

  factory.getClientsAvailableProducts = () => (dispatch, getState) => {
    const state = getState();
    const clientId = contextSelectors.selectClientId(state);
    const inContextSwitch = contextSelectors.selectInSwitchContext(state);

    // TODO: role maybe in context?
    const roleId = userSelectors.selectRoleId(state);

    const getClientProductsPromise = productEventApi.getClientProducts(clientId);

    productEventApi.getClientProducts(clientId)
      .then((products) => {
        if (inContextSwitch) {
          dispatch(factory.getClientsAvailableProductsSuccess(products));
        } else {
          // sense the response from the users role requests products doesnt have a human
          // readable format for product name, you have to get the human readable name from
          // the clients products in the above request for getClientProducts
          productEventApi.getUsersProductGroup(clientId, roleId)
            .then((productGroup) => {
              const usersProducts = productGroup.products;

              // store the filtered products with human readable names
              const formattedProductList = [];
              for (let i = 0; i < usersProducts.length; i++) {
                const product = usersProducts[i];
                for (let j = 0; j < products.length; j++) {
                  if (products[j].id === product.id) {
                    formattedProductList.push(products[j]);
                  }
                }
              }

              productEventApi.getClientProducts(clientId).then((productList) => {
                const autoWeatherProducts = productList.filter((p) => {
                  if (AUTO_WEATHER_PRODUCT_TYPES.indexOf(p.id) !== -1) {
                    return p;
                  }

                  return undefined;
                }).sort((a, b) => {
                  // get the index value and sort off that
                  const aIndex = AUTO_WEATHER_PRODUCT_TYPES.indexOf(a.id);
                  const bIndex = AUTO_WEATHER_PRODUCT_TYPES.indexOf(b.id);

                  return aIndex > bIndex ? 1 : -1;
                });

                const filteredProductList = formattedProductList.filter((p) => {
                  if (
                    ENABLED_PRODUCT_TYPES.indexOf(p.id) !== -1
                    && AUTO_WEATHER_PRODUCT_TYPES.indexOf(p.id) === -1
                  ) {
                    return p;
                  }

                  return undefined;
                });

                const combined = filteredProductList.concat(autoWeatherProducts);
                dispatch(factory.getClientsAvailableProductsSuccess(combined));
              });
            });
        }
      });
  };

  factory.updateProductDetails = (productDetails) => ({
    type: constants.UPDATE_PRODUCT_DETAILS,
    details: productDetails,
  });

  factory.appendProductDetails = (productDetails) => ({
    type: constants.APPEND_PRODUCT_DETAILS,
    details: productDetails,
  });

  factory.getProductEventDetails = (productEvent) => {
    const {
      content,
    } = productEvent;
    const {
      productDetailsUrl,
    } = content;

    return axios.get(productDetailsUrl);
  };

  factory.setCurrentProductLoading = (loading) => ({
    type: constants.SET_CURRENT_PRODUCT_LOADING,
    loading,
  });

  factory.updateProductEvent = (productEvent, stateUpdate = false) => (dispatch, getState) => {
    const state = getState();
    const productEvents = productsSelectors.selectProductEvents(state);
    let currentProduct = currentProductSelectors.selectEvent(state);

    let updatedIndex = null;
    for (let i = 0; i < productEvents.length; i++) {
      const productEventCheck = productEvents[i];
      if (productEventCheck.id === productEvent.id) {
        updatedIndex = i;
        break;
      }
    }

    if (currentProduct !== null) {
      if (currentProduct.id === productEvent.id) {
        dispatch(factory.setCurrentProductLoading(true));
      }
    }

    // get the product details
    const productDetailsPromise = factory.getProductEventDetails(productEvent);

    // check if we should be appending or replacing the product event/details
    let methodAction = null;
    if (updatedIndex !== null) {
      dispatch({
        type: constants.UPDATE_PRODUCT_EVENT,
        index: updatedIndex,
        event: productEvent,
      });

      methodAction = factory.updateProductDetails;
    } else {
      // didnt find the product event so it must be a new one,
      // append it to event list and get details
      dispatch({
        type: constants.APPEND_PRODUCT_EVENT,
        event: productEvent,
      });

      methodAction = factory.appendProductDetails;
    }

    productDetailsPromise.then((response) => {
      const {
        data: productDetails,
      } = response;

      dispatch(methodAction(productDetails));
      // need to update the current product if its a relevent update
      currentProduct = currentProductSelectors.selectEvent(state);
      // check if the current product is event set
      if (currentProduct != null) {
        if (currentProduct.id === productEvent.id) {
          dispatch(factory.getCurrentProductEventSuccess(productEvent));
          dispatch(factory.getCurrentProductDetailsSuccess(productDetails));
          dispatch(factory.getCurrentProductNotifications());
          dispatch(factory.setAckInProgress(false));
          dispatch(factory.setCurrentProductLoading(false));

          if (stateUpdate) {
            dispatch(factory.setCurrentProductLoadingRead(false));
          }
        }
      }
    });
  };

  factory.setCurrentProductLoadingRead = (loading) => ({
    type: constants.SET_CURRENT_PRODUCT_LOADING_READ,
    loading,
  });

  factory.removeProductDetails = (productEventId) => ({
    type: constants.REMOVE_PRODUCT_DETAILS,
    productEventId,
  });

  factory.removeProductEvent = (productEventId) => ({
    type: constants.REMOVE_PRODUCT_EVENT,
    productEventId,
  });

  factory.newPubSubProductEvent = (productEvent, topic) => ({
    type: constants.NEW_PUB_SUB_PRODUCT_EVENT,
    event: {
      payload: productEvent,
      topic,
    },
  });

  factory.productEventStoppedPlayingSound = (productEventId, looped) => ({
    type: constants.PRODUCT_EVENT_SOUND_STOPPED,
    productEventId,
    looped,
  });

  factory.playProductEventSound = (productEvent) => ({
    type: constants.PRODUCT_EVENT_SOUND_STARTED,
    productEvent,
  });

  factory.setCurrentProductAsRead = () => (dispatch, getState) => {
    const state = getState();
    const productEventId = currentProductSelectors.selectEvent(state).id;
    const userId = contextSelectors.selectUserId(state);
    const clientId = contextSelectors.selectClientId(state);
    const inContextSwitch = contextSelectors.selectInSwitchContext(state);

    if (!inContextSwitch) {
      dispatch({ type: constants.SET_CURRENT_PRODUCT_AS_READ });
      productEventApi.readProductEvent(productEventId, clientId, userId)
        .then(null, () => {
          dispatch({ type: constants.SET_CURRENT_PRODUCT_AS_READ_FAILED });
        });
    }
  };

  factory.setAckInProgress = (ackInProgress) => ({
    type: constants.SET_CURRENT_PRODUCT_ACK_IN_PROGRESS,
    ackInProgress,
  });

  factory.getCurrentProductNotificationSuccess = (notifications) => ({
    type: constants.GET_CURRENT_PRODUCT_VERSIONS,
    versions: notifications,
  });

  factory.getCurrentProductNotifications = () => (dispatch, getState) => {
    const state = getState();
    const productEventId = currentProductSelectors.selectEvent(state).id;
    const clientId = contextSelectors.selectClientId(state);
    const inContextSwitch = contextSelectors.selectInSwitchContext(state);
    const { productId } = currentProductSelectors.selectEvent(state).content;
    const criteriaFilter = getProductNotificationCriteriaFilter(productId);

    // change location set if in switch context
    let promise = null;
    if (!inContextSwitch) {
      const locationSetId = userSelectors.selectLocationSetId(state);
      promise = getProductNotifications(clientId, productEventId, locationSetId, criteriaFilter);
    } else {
      promise = getClientProductNotifications(clientId, productEventId, criteriaFilter);
    }

    promise.then((response) => {
      const { data } = response;
      dispatch(factory.getCurrentProductNotificationSuccess(data));
    });
  };

  return factory;
};

export default productEventActions();
